import { HttpErrorResponse } from '@angular/common/http';
import { enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import * as Sentry from '@sentry/angular';
import { browserTracingIntegration } from '@sentry/angular';
import { AppComponent } from './app/app.component';
import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

Sentry.init({
  // Sentry should be disabled when working locally.
  enabled: environment.envMode !== 'local',

  /**
   * Temporarily having separate DSNs (Data Source Name) for driving and real estate verticals.
   * TODO: Once RE and DRV are fully migrated, switch to only using the aceable-vision DSN.
   */
  dsn: environment.sentryDsn,

  // Environment name to pass to Sentry.
  environment: environment.envMode === 'prod' ? 'production' : 'dev',

  integrations: [
    // Used to monitor its performance.
    browserTracingIntegration(),
  ],

  initialScope: {
    user: { ip_address: '{{auto}}' },
  },

  // The sample rate for error events, 0.2 corresponds to 20% of errors will be sent
  sampleRate: 1.0,

  // The percentage chance a given transaction will be sent to Sentry
  tracesSampleRate: environment.envMode === 'prod' ? 0.2 : 1.0,

  // Enable trace propagation for distributed tracing
  propagateTraceparent: true,

  // Keep at root level as well for compatibility
  tracePropagationTargets: [
    environment.xgritApiConfig.url, // XGrit API (includes env-specific domain)
    environment.craftApiUrl, // Craft CMS GraphQL API (includes gqlproxy for stage)
    environment.blogCraftApiUrl, // Blog Craft CMS GraphQL API
    'localhost',
    /^\//, // Relative URLs
  ],

  /**
   * Used to tag releases.
   * Format: YYYY-MM-DDTHH (e.g., 2025-11-14T15)
   * Matches the release format used when uploading sourcemaps to Sentry.
   */
  release: new Date().toISOString().slice(0, 13),

  denyUrls: [/verint-cdn\.com/i],

  ignoreErrors: [/AdCloudEvent/],

  beforeSend(event, hint) {
    const error = hint.originalException;

    // Avoid logging network error since they are handled in the HttpErrorInterceptor with more details
    // Only applies to our primary Xgrit API until we can confirm more use cases.
    // AARP runs on its own first-party host (aarpdriversafety.org) so we match both registrable domains.
    if (
      error instanceof HttpErrorResponse &&
      error.status === 400 &&
      (error.url?.includes('xgritapi.com') || error.url?.includes('aarpdriversafety.org'))
    ) {
      return null;
    }

    // Ignore 401 errors from user/sync-marketing endpoint (user not logged in - expected behavior)
    if (error instanceof HttpErrorResponse && error.status === 401 && error.url?.includes('user/sync-marketing')) {
      return null;
    }

    // Google Analytics — loaded at runtime as a Segment device-mode destination, not by
    // core-mkt code — can throw `SyntaxError: Failed to execute 'insertBefore' on 'Node'`
    // while injecting its own tracking markup built from Commission Junction / partner utm
    // params. The throw originates in that third-party script (an anonymous `/**/` frame),
    // does not affect page rendering, and is pure noise. Drop it — but only when the GA
    // tracking signature is present, so a genuine in-app insertBefore failure is never
    // masked. We scan only the fields that can carry the signature (each exception `value`
    // and stack-frame `filename`) rather than serializing the whole exception, and match on
    // the GA-specific markers `ga_utm_` / `available_ga` — not the generic `utm_source=cj`
    // referral param, which appears on legitimate CJ-affiliate pages unrelated to GA.
    const signatureFields =
      event.exception?.values?.flatMap((value) => [
        value.value ?? '',
        ...(value.stacktrace?.frames?.map((frame) => frame.filename ?? '') ?? []),
      ]) ?? [];
    const hasInsertBefore = signatureFields.some((field) => field.includes('insertBefore'));
    const hasGaSignature = signatureFields.some((field) => /ga_utm_|available_ga/.test(field));
    if (hasInsertBefore && hasGaSignature) {
      return null;
    }

    return event;
  },
});

if (environment.production) {
  enableProdMode();
}

document.addEventListener('DOMContentLoaded', () => {
  bootstrapApplication(AppComponent, {
    providers: [provideZoneChangeDetection(), importProvidersFrom(AppModule)],
    // eslint-disable-next-line no-console
  }).catch((err) => console.error(err));
});
